import type { InsightsTaskItem, InsightsTaskList } from "../types";
import type { InsightsTranscriptRow } from "./group-tool-stack";
import { parseInsightsAnswerSegments } from "./parse-render-blocks";

export const PROVISIONAL_TASK_ID = "__provisional__";

export type TaskSectionChild =
  | { kind: "tool-stack"; row: Extract<InsightsTranscriptRow, { type: "tool-stack" }> }
  | { kind: "ledger"; id: string; content: string; live?: boolean }
  | { kind: "assistant-prose"; id: string; text: string; streaming?: boolean }
  | { kind: "user"; id: string; text: string };

export type InvestigationTaskSection = {
  id: string;
  taskId: string;
  title: string;
  status: InsightsTaskItem["status"] | "provisional";
  activeForm?: string;
  startedAt?: string;
  completedAt?: string;
  durationMs?: number;
  active: boolean;
  children: TaskSectionChild[];
  toolCount: number;
  noteCount: number;
};

export type InvestigationTaskViewRow =
  | { type: "user"; id: string; text: string; investigationId?: string }
  | { type: "task"; section: InvestigationTaskSection }
  | {
      type: "final-answer";
      id: string;
      text: string;
      streaming?: boolean;
      evidenceLine?: string | null;
    };

function splitAssistantRow(
  row: Extract<InsightsTranscriptRow, { type: "assistant" }>,
): Array<
  | { kind: "ledger"; id: string; content: string; live?: boolean }
  | { kind: "assistant-prose"; id: string; text: string; streaming?: boolean }
> {
  const segments = parseInsightsAnswerSegments(row.text);
  const out: Array<
    | { kind: "ledger"; id: string; content: string; live?: boolean }
    | { kind: "assistant-prose"; id: string; text: string; streaming?: boolean }
  > = [];
  let proseParts: string[] = [];
  const flushProse = (index: number) => {
    const text = proseParts.join("");
    proseParts = [];
    if (!text.trim()) return;
    out.push({
      kind: "assistant-prose",
      id: `${row.id}:prose:${index}`,
      text,
      streaming: row.streaming,
    });
  };

  segments.forEach((seg, index) => {
    if (seg.type === "ledger") {
      flushProse(index);
      out.push({
        kind: "ledger",
        id: `${row.id}:ledger:${index}`,
        content: seg.content,
        live: !seg.complete,
      });
      return;
    }
    if (seg.type === "prose") {
      proseParts.push(seg.text);
      return;
    }
    if (seg.type === "render") {
      // Keep incomplete renders open so InsightsAnswer can stream-fill widgets.
      proseParts.push(seg.complete ? `<render>${seg.html}</render>` : `<render>${seg.html}`);
    }
  });
  flushProse(segments.length);
  if (out.length === 0 && row.text.trim()) {
    out.push({
      kind: "assistant-prose",
      id: `${row.id}:prose:0`,
      text: row.text,
      streaming: row.streaming,
    });
  }
  return out;
}

function taskMeta(
  taskId: string,
  taskList: InsightsTaskList | null | undefined,
): Pick<
  InvestigationTaskSection,
  "title" | "status" | "activeForm" | "startedAt" | "completedAt" | "durationMs"
> {
  if (taskId === PROVISIONAL_TASK_ID) {
    return { title: "Start Investigation", status: "provisional" };
  }
  const task = taskList?.tasks.find((t) => t.id === taskId);
  if (task) {
    return {
      title: task.title,
      status: task.status,
      activeForm: task.activeForm,
      startedAt: task.startedAt,
      completedAt: task.completedAt,
      durationMs: task.durationMs,
    };
  }
  return { title: "Investigation step", status: "pending" };
}

function emptySection(
  taskId: string,
  taskList: InsightsTaskList | null | undefined,
): InvestigationTaskSection {
  const meta = taskMeta(taskId, taskList);
  return {
    id: `task-panel-${taskId}`,
    taskId,
    ...meta,
    active: meta.status === "in_progress",
    children: [],
    toolCount: 0,
    noteCount: 0,
  };
}

function stackTaskId(
  row: Extract<InsightsTranscriptRow, { type: "tool-stack" }>,
): string | undefined {
  for (const a of row.activities) {
    if (a.taskId) return a.taskId;
  }
  return undefined;
}

/**
 * Map a stamped tool taskId onto the current rail. Unknown ids (prior-inv bleed)
 * must not open untitled "Investigation step" panels — fold into provisional instead.
 * Callers: groupInvestigationTasks tool-stack path (InsightsChat).
 * User: "still shows multiple investigation step sections, not linked to any task."
 */
function resolvePanelTaskId(
  stamped: string | undefined,
  lastTaskId: string | null,
  taskList: InsightsTaskList | null | undefined,
): string {
  const fallback = lastTaskId ?? PROVISIONAL_TASK_ID;
  if (!stamped) return fallback;
  if (stamped === PROVISIONAL_TASK_ID) return PROVISIONAL_TASK_ID;
  const known = taskList?.tasks;
  // Before the first task_write, taskList is null — never trust a stamped id
  // (prior-inv bleed opens untitled "Investigation step" panels).
  if (!known?.length) return fallback;
  if (known.some((t) => t.id === stamped)) return stamped;
  return PROVISIONAL_TASK_ID;
}

/** Fold user lines when open tasks or pre-task provisional work exist (no ack-word regex). */
function shouldPreserveTaskTurn(params: {
  hasOpenTasks: boolean;
  taskListNull: boolean;
  lastTaskId: string | null;
  seenTools: boolean;
}): boolean {
  return (
    params.hasOpenTasks || (params.taskListNull && (params.lastTaskId != null || params.seenTools))
  );
}

function isHostFinalAnswer(row: Extract<InsightsTranscriptRow, { type: "assistant" }>): boolean {
  const k = row.assistantKind;
  return k === "final_answer" || k === "closer";
}

/** Legacy fallback when host did not stamp assistantKind (pre-structured rows). */
function isSubstantialFinalProse(text: string): boolean {
  const t = text.trim();
  return t.length > 80 && !t.includes("<ledger>");
}

/** Ended-run short closer with a trailing ledger in the same assistant message. */
function isEndedTurnCloserWithOrphans(
  lastProse: { text: string; streaming?: boolean } | undefined,
  parts: Array<{ kind: string }>,
  opts: { running: boolean; isAfterLastUser: boolean },
): boolean {
  if (opts.running || !opts.isAfterLastUser || !lastProse || lastProse.streaming) return false;
  const t = lastProse.text.trim();
  if (!t || t.includes("<ledger>")) return false;
  return parts.some((p) => p.kind === "ledger");
}

function appendOrphanToTaskInView(
  view: InvestigationTaskViewRow[],
  taskId: string,
  child: TaskSectionChild,
): void {
  for (let j = view.length - 1; j >= 0; j -= 1) {
    const row = view[j];
    if (row?.type !== "task" || row.section.taskId !== taskId) continue;
    row.section.children.push(child);
    if (child.kind === "tool-stack") {
      row.section.toolCount += child.row.activities.length;
    }
    if (child.kind === "ledger") row.section.noteCount += 1;
    return;
  }
}

/**
 * Group transcript into task panels (host-stamped taskId on tools).
 * Orphan work before the first task lands in a provisional panel.
 * User messages fold into the active task (no panel split on HITL);
 * the opening question is buffered into the first task so it stays visible.
 * A non-Continue follow-up starts a new turn: prior final answer peels out,
 * then the new question seeds the next task panels.
 */
export function groupInvestigationTasks(
  rows: InsightsTranscriptRow[],
  options?: {
    running?: boolean;
    taskList?: InsightsTaskList | null;
  },
): InvestigationTaskViewRow[] {
  const running = Boolean(options?.running);
  const taskList = options?.taskList ?? null;
  const view: InvestigationTaskViewRow[] = [];

  const buckets = new Map<string, InvestigationTaskSection>();
  const order: string[] = [];
  let lastTaskId: string | null = null;
  let lastRealTaskId: string | null = null;
  let terminalTurnClosed = false;
  let seenTools = false;
  // Same task can emit again after a hard flush (e.g. final answer); keep ids unique.
  const emitCounts = new Map<string, number>();
  const pendingUsers: Array<{ id: string; text: string; investigationId?: string }> = [];

  const ensure = (taskId: string) => {
    if (buckets.has(taskId)) return buckets.get(taskId)!;
    const section = emptySection(taskId, taskList);
    // Opening / buffered questions land at the top of the first task that opens.
    if (pendingUsers.length) {
      for (const u of pendingUsers) {
        section.children.push({ kind: "user", id: u.id, text: u.text });
      }
      pendingUsers.length = 0;
    }
    buckets.set(taskId, section);
    order.push(taskId);
    return section;
  };

  const pushChild = (taskId: string, child: TaskSectionChild) => {
    if (terminalTurnClosed && taskId === PROVISIONAL_TASK_ID) {
      if (lastRealTaskId) {
        appendOrphanToTaskInView(view, lastRealTaskId, child);
      }
      return;
    }
    const section = ensure(taskId);
    section.children.push(child);
    if (child.kind === "tool-stack") {
      section.toolCount += child.row.activities.length;
    }
    if (child.kind === "ledger") section.noteCount += 1;
    if (taskId !== PROVISIONAL_TASK_ID) lastRealTaskId = taskId;
  };

  const flushPendingUsersAsRows = () => {
    while (pendingUsers.length) {
      const u = pendingUsers.shift()!;
      view.push({
        type: "user",
        id: u.id,
        text: u.text,
        ...(u.investigationId ? { investigationId: u.investigationId } : {}),
      });
    }
  };

  const flushBuckets = () => {
    // Refresh titles/status from latest taskList
    for (const id of order) {
      const section = buckets.get(id);
      if (!section) continue;
      const meta = taskMeta(id, taskList);
      section.title = meta.title;
      section.status = meta.status;
      section.activeForm = meta.activeForm;
      section.startedAt = meta.startedAt;
      section.completedAt = meta.completedAt;
      section.durationMs = meta.durationMs;
      section.active = meta.status === "in_progress";
    }

    const emitOrder: string[] = [];
    if (order.includes(PROVISIONAL_TASK_ID)) emitOrder.push(PROVISIONAL_TASK_ID);
    for (const t of taskList?.tasks ?? []) {
      if (order.includes(t.id) && !emitOrder.includes(t.id)) emitOrder.push(t.id);
    }
    for (const id of order) {
      if (!emitOrder.includes(id)) emitOrder.push(id);
    }

    for (const id of emitOrder) {
      const section = buckets.get(id);
      if (!section || section.children.length === 0) continue;
      if (terminalTurnClosed && id === PROVISIONAL_TASK_ID) {
        if (lastRealTaskId) {
          for (const child of section.children) {
            appendOrphanToTaskInView(view, lastRealTaskId, child);
          }
        }
        continue;
      }
      const n = (emitCounts.get(id) ?? 0) + 1;
      emitCounts.set(id, n);
      section.id = `task-panel-${id}-${n}`;
      view.push({ type: "task", section });
    }

    buckets.clear();
    order.length = 0;
    // Keep lastTaskId so a Continue after tools can still target the same task.
  };

  const hasLaterUser = (fromIdx: number): boolean => {
    for (let j = fromIdx + 1; j < rows.length; j += 1) {
      if (rows[j]!.type === "user") return true;
    }
    return false;
  };

  let lastUserIdx = -1;
  rows.forEach((r, i) => {
    if (r.type === "user") lastUserIdx = i;
  });

  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i]!;
    if (row.type === "user") {
      const hasOpenTasks = Boolean(
        taskList?.tasks.some((t) => t.status === "pending" || t.status === "in_progress"),
      );
      // Keep one panel for open tasks and pre-task_list provisional HITL.
      // Finished-turn follow-ups still preserve when host keeps the same investigation;
      // only flush when there is no open work and no provisional panel state.
      const preserveTurn = shouldPreserveTaskTurn({
        hasOpenTasks,
        taskListNull: taskList == null,
        lastTaskId,
        seenTools,
      });
      const turnAlreadyStarted =
        Boolean(lastTaskId) || seenTools || order.length > 0 || buckets.size > 0;
      if (!preserveTurn && turnAlreadyStarted) {
        flushBuckets();
        lastTaskId = null;
        lastRealTaskId = null;
        terminalTurnClosed = false;
        seenTools = false;
        pendingUsers.push({
          id: row.id,
          text: row.text,
          ...(row.investigationId ? { investigationId: row.investigationId } : {}),
        });
        continue;
      }
      // New user after a peeled final: stop folding untagged tools into prior task.
      if (terminalTurnClosed) {
        terminalTurnClosed = false;
        lastRealTaskId = null;
      }
      // After tools have run, Continue/HITL can land before the next stamp — prefer
      // the rail's in_progress task. Do not do this on the opening question.
      if (!lastTaskId && seenTools) {
        const current = taskList?.tasks.find((t) => t.status === "in_progress")?.id;
        if (current) lastTaskId = current;
      }
      if (lastTaskId) {
        pushChild(lastTaskId, { kind: "user", id: row.id, text: row.text });
      } else if (order.length > 0) {
        const tid = order[order.length - 1]!;
        pushChild(tid, { kind: "user", id: row.id, text: row.text });
      } else {
        pendingUsers.push({
          id: row.id,
          text: row.text,
          ...(row.investigationId ? { investigationId: row.investigationId } : {}),
        });
      }
      continue;
    }

    if (row.type === "tool-stack") {
      seenTools = true;
      const stamped = stackTaskId(row);
      const tid = resolvePanelTaskId(stamped, lastTaskId, taskList);
      lastTaskId = tid;
      pushChild(tid, { kind: "tool-stack", row });
      continue;
    }

    const parts = splitAssistantRow(row);
    const isAfterLastUser = i > lastUserIdx;
    const proseParts = parts.filter((p) => p.kind === "assistant-prose");
    const lastProse = proseParts.at(-1);
    // Peel turn finals even while a follow-up is running: the prior answer sits
    // before the latest user message and must not stay inside the last old task.
    const hostFinal = Boolean(lastProse) && isHostFinalAnswer(row);
    const isFinalAnswer =
      Boolean(lastProse) &&
      !lastProse!.streaming &&
      (hostFinal ||
        isSubstantialFinalProse(lastProse!.text) ||
        isEndedTurnCloserWithOrphans(lastProse, parts, {
          running,
          isAfterLastUser,
        })) &&
      (hostFinal || (!running && isAfterLastUser) || hasLaterUser(i));

    for (let pi = 0; pi < parts.length; pi += 1) {
      const part = parts[pi]!;
      if (isFinalAnswer && part.kind === "assistant-prose" && part.id === lastProse!.id) {
        flushBuckets();
        flushPendingUsersAsRows();
        view.push({
          type: "final-answer",
          id: part.id,
          text: part.text,
          streaming: part.streaming,
        });
        lastTaskId = null;
        seenTools = false;
        // Fold trailing ledger/notes on every final peel (incl. mid-thread).
        terminalTurnClosed = true;
        for (let oi = pi + 1; oi < parts.length; oi += 1) {
          const orphan = parts[oi]!;
          if (orphan.kind === "assistant-prose" && !orphan.text.trim()) continue;
          if (lastRealTaskId) {
            appendOrphanToTaskInView(view, lastRealTaskId, orphan);
          }
        }
        break;
      }
      if (part.kind === "assistant-prose" && !part.text.trim()) continue;
      if (!lastTaskId && seenTools) {
        const current = taskList?.tasks.find((t) => t.status === "in_progress")?.id;
        if (current) lastTaskId = current;
      }
      const tid = lastTaskId ?? PROVISIONAL_TASK_ID;
      lastTaskId = tid;
      pushChild(tid, part);
    }
  }

  flushBuckets();
  flushPendingUsersAsRows();

  // Align chat "active" with Tasks rail (taskList.in_progress). Callers: InsightsChat.
  // User report: Continue left chat on census while rail showed gather.
  if (running) {
    const inProgressId = taskList?.tasks.find((t) => t.status === "in_progress")?.id;
    if (inProgressId) {
      for (const row of view) {
        if (row.type === "task") {
          row.section.active = row.section.taskId === inProgressId;
        }
      }
    }
  }

  return view;
}

/** DOM id for chat task panel / rail scroll targets. */
export function insightsTaskPanelDomId(taskId: string): string {
  return `insights-task-${taskId}`;
}
